import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  ListItemContent,
  Stack,
  Typography,
} from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { arrayMoveImmutable } from 'array-move';
import { useEffect, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import ArrowIcon from '@/assets/icons/arrow.svg?react';
import OrientationIcon from '@/assets/icons/orientation-arrow.svg?react';
import { FloatingInput } from '@/components/form-elements/FloatingInput';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { SignCodeIcon } from '@/components/signs/SignCodeIcon';
import { EditModalDialog } from '@/components/signs/modal/EditModalDialog';
import { useBearingEstimation } from '@/hooks/useBearingEstimation';
import { I18N_DE } from '@/i18n/de';
import { SignGroupInstallationSide, SignGroupType, SignStatusType, UpdateSignGroupDto } from '@/modules/api';
import { backendApiSignGroupsUpdateMutation } from '@/modules/api/@tanstack/react-query.gen';
import { useSignGroupQuery } from '@/queries/useSignGroupQuery';
import { useCreateSignStore } from '@/stores/useCreateSignStore';

export const Route = createFileRoute(
  '/_authenticated/_management/verkehrszeichen/$adminLevelId/$signSupportId_/$signGroupId/bearbeiten',
)({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: SignGroupEditRoute,
});

type FormData = Pick<UpdateSignGroupDto, 'bearing' | 'installationSide' | 'type' | 'roadsideDistance' | 'sibId'>;

function SignGroupEditRoute() {
  const formId = useId();
  const navigate = useNavigate();
  const { adminLevelId, signSupportId, signGroupId } = Route.useParams();
  const { reloadSupportLayer } = useCreateSignStore((state) => state.actions);

  const { data: group } = useSignGroupQuery();

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  const [signsOrder, setSignsOrder] = useState<{ id: string; code: string; status: SignStatusType }[]>();
  const currentBearing = watch('bearing');
  const bearingEstimations = useBearingEstimation();

  useEffect(() => {
    reset({
      ...group,
      ...(group?.roadsideDistance && {
        roadsideDistance: group.roadsideDistance / 100,
      }),
    });
    setSignsOrder(group?.signs?.map(({ id, code, status }) => ({ id, code, status })));
  }, [group, reset]);

  const { mutate, error, isPending } = useMutation({
    ...backendApiSignGroupsUpdateMutation(),
    onSuccess: async () => {
      reloadSupportLayer();
      navigate({
        to: '/verkehrszeichen/$adminLevelId/$signSupportId/$signGroupId',
        params: {
          adminLevelId,
          signSupportId,
          signGroupId,
        },
      });
    },
  });

  const compareOrder = () =>
    JSON.stringify(signsOrder) === JSON.stringify(group?.signs?.map(({ id, code }) => ({ id, code })));

  return (
    <EditModalDialog title="Verkehrszeichen-Gruppe bearbeiten">
      <Stack
        component="form"
        id={formId}
        onSubmit={handleSubmit(({ type, bearing, installationSide, roadsideDistance }) => {
          if (isPending) return;

          mutate({
            path: { signGroupId: group?.id! },
            body: {
              type,
              bearing,
              installationSide,
              ...(roadsideDistance && {
                roadsideDistance: roadsideDistance * 100,
              }),
              ...(!compareOrder() && {
                signsOrder: signsOrder?.map(({ id }) => id),
              }),
            },
          });
        })}
        gap={2}
      >
        <FormControl>
          <FormLabel>Reihenfolge</FormLabel>
          <Box>
            {signsOrder?.map(({ id, code, status }, index, array) => (
              <Stack key={id} direction="row">
                <Stack direction="column" sx={{ margin: 'auto' }}>
                  {index !== 0 && (
                    <Button
                      variant="plain"
                      size="sm"
                      onClick={() => {
                        setSignsOrder(arrayMoveImmutable(array, index, index - 1));
                      }}
                    >
                      <ArrowIcon style={{ transform: 'rotate(-180deg)' }} />
                    </Button>
                  )}
                  {index !== array.length - 1 && (
                    <Button
                      variant="plain"
                      size="sm"
                      onClick={() => {
                        setSignsOrder(arrayMoveImmutable(array, index, index + 1));
                      }}
                    >
                      <ArrowIcon />
                    </Button>
                  )}
                </Stack>
                <SignCodeIcon
                  code={code}
                  style={{ width: 40, height: 40, margin: 'auto' }}
                  deleted={status === 'DELETED'}
                />
                <ListItemContent sx={{ paddingLeft: 2, margin: 'auto' }}>
                  <Typography level="body-sm">{code}</Typography>
                </ListItemContent>
              </Stack>
            ))}
          </Box>
        </FormControl>

        <FormControl error={!!errors.type}>
          <FormLabel>Typ</FormLabel>
          <Controller
            control={control}
            name="type"
            render={({ field }) => (
              <RadioWithDescriptionGroup
                {...field}
                orientation="horizontal"
                options={Object.values(SignGroupType)}
                optionLabel={({ option }) => I18N_DE.SignGroupType[option]}
              />
            )}
          />
        </FormControl>

        <FormControl error={!!errors.installationSide}>
          <FormLabel>Aufstellungsseite</FormLabel>
          <Controller
            control={control}
            name="installationSide"
            render={({ field }) => (
              <RadioWithDescriptionGroup
                {...field}
                orientation="horizontal"
                options={Object.values(SignGroupInstallationSide)}
                optionLabel={({ option }) => I18N_DE.SignGroupInstallationSide[option]}
              />
            )}
          />
        </FormControl>

        <FormControl error={!!errors.bearing}>
          <FormLabel>Abstand zum Fahrbahnrand</FormLabel>
          <Stack direction="row">
            <Input
              type="number"
              variant="outlined"
              sx={{ width: 150 }}
              slotProps={{
                input: {
                  ...register('roadsideDistance'),
                  label: 'optional',
                  step: 0.1,
                },
              }}
              endDecorator="Meter"
            />
          </Stack>
        </FormControl>

        <FormControl error={!!errors.sibId}>
          <FormLabel>Straßen-Kennung (SIB ID)</FormLabel>
          <Input
            type="text"
            variant="outlined"
            slots={{
              input: FloatingInput,
            }}
            slotProps={{
              input: {
                ...register('sibId'),
                label: 'optional',
              },
            }}
          />
        </FormControl>

        <FormControl error={!!errors.bearing}>
          <FormLabel>Ausrichtung</FormLabel>
          <Stack direction="row" gap={1}>
            <Input
              type="number"
              variant="outlined"
              sx={{ width: 150 }}
              slotProps={{
                input: {
                  ...register('bearing'),
                  label: 'optional',
                  min: 0,
                  max: 359,
                  step: 1,
                },
              }}
              endDecorator="° Grad"
            />
            {currentBearing ? (
              <OrientationIcon
                style={{
                  transform: `rotate(${currentBearing}deg)`,
                  width: 30,
                  height: 30,
                  margin: 'auto 20px',
                }}
              />
            ) : (
              bearingEstimations.map((estiamtion) => (
                <Button key={estiamtion} variant="outlined" onClick={() => setValue('bearing', estiamtion)}>
                  <OrientationIcon
                    style={{
                      transform: `rotate(${estiamtion}deg)`,
                      width: 30,
                      height: 30,
                    }}
                  />
                </Button>
              ))
            )}
          </Stack>
        </FormControl>
        {error && <FormHelperText>{error.message}</FormHelperText>}

        <Stack direction="row" gap={2}>
          <Button type="submit" form={formId} loading={isPending} disabled={isPending}>
            Speichern
          </Button>
        </Stack>
      </Stack>
    </EditModalDialog>
  );
}
